/** @jsxImportSource @emotion/react */
import "react-phone-number-input/style.css";

import { SyncOutlined } from "@ant-design/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { Form, notification, Popover } from "antd";
import { RegisterFormType } from "api/auth/type";
import { BG_MAIN } from "assets/Common";
import backgroundLogin from "assets/temporary/images/list_bg_2.jpg";
import InputCustom from "components/Common/InputCustom";
import InputPassword from "components/Common/InputPassword";
import InputPhoneNumber from "components/Common/InputPhoneNumber";
import { ModalCustom } from "components/Common/ModalCustom";
import {
  REGEX_FULLNAME,
  REGEX_PASSWORD,
  REGEX_USERNAME_REGISTER,
} from "contants/regex";
import { ResponseCode } from "contants/response";
import { useAppDispatch, useAppSelector } from "hooks/app-hook";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { selectIsLoading } from "store/auth";
import { registerMemberDetail } from "store/auth/auth.thunk";
import { selectOpenModal, setOpenModal } from "store/modal";
import { StatusNotification } from "types";
import * as yup from "yup";

import { LogoAuth } from "../LogoAuth";
import { SignupModalStyle } from "./index.style";

interface RegisterForm extends RegisterFormType {
  approve: boolean;
}

interface props {
  isCode: string | null;
}
export const SignupModal = ({ isCode }: props) => {
  const dispatch = useAppDispatch();
  const [_, setParams] = useSearchParams();
  const { t } = useTranslation(["login", "placeholder", "button"]);

  const openModal = useAppSelector(selectOpenModal);
  const isLoading = useAppSelector(selectIsLoading);

  const [isFocusFullname, setIsFocusFullname] = useState<boolean>();

  const schema = yup
    .object({
      username: yup
        .string()
        .trim()
        .matches(
          REGEX_USERNAME_REGISTER,
          t("validate:UsernameValid", { min: 6, max: 12 })
        )
        .required(t("validate:UsernameRequired")),
      fullname: yup
        .string()
        .trim()
        .matches(REGEX_FULLNAME, t("validate:FullnamneValid"))
        .required(t("validate:FieldRequired")),
      mobile: yup
        .string()
        .min(10, t("validate:Min"))
        .max(14, t("validate:Max"))
        .trim()
        .required(t("validate:MobileRequired")),

      password: yup
        .string()
        .trim()
        .matches(REGEX_PASSWORD, t("validate:PasswordValid"))
        .required(t("validate:PasswordValid")),
      confirm_password: yup
        .string()
        .trim()
        .matches(REGEX_PASSWORD, t("validate:PasswordConfirm"))
        .oneOf([yup.ref("password")], t("validate:PasswordConfirm"))
        .required(t("validate:PasswordConfirm")),
      referral: yup.string().trim(),
    })
    .required();

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    watch,
  } = useForm<RegisterForm>({
    resolver: yupResolver<any>(schema),
    mode: "all",
  });

  const [disableInput] = useState<boolean>(false);

  const [api, contextHolder] = notification.useNotification();

  const openNotification = (status: StatusNotification, message: string) => {
    api[status]({
      message,
      description: "",
    });
  };

  const AreaCodeValidate = (number: string) => {
    if (number.startsWith("+")) {
      number = number.slice(1);
    }
    return number;
  };

  const onSubmit = async (data: RegisterForm) => {
    const dataForm = {
      ...data,
      mobile: data.mobile && AreaCodeValidate(data.mobile),
      username: data.username.toLocaleLowerCase(),
    };
    const res: any = await dispatch(
      registerMemberDetail({ payload: dataForm })
    );

    const { status, msg } = res.payload;

    if (status !== ResponseCode.SUCCESS) {
      openNotification("error", msg);
      return;
    }

    openNotification("success", msg);
    dispatch(setOpenModal(""));
    setParams({});
  };

  const handleCloseModal = () => {
    dispatch(setOpenModal(""));
  };

  useEffect(() => {
    if (isCode) {
      setValue("referral", isCode);
    }
  }, [isCode]);

  return (
    <ModalCustom
      isOpenModal={openModal.includes("signup")}
      handleCloseModal={handleCloseModal}
      titleModal={t("Register")}
      headerBackground={BG_MAIN}
      heightBackground={75}
      bodyModal={
        <div style={{ overflow: "auto" }}>
          <LogoAuth />

          <Form
            css={SignupModalStyle.formContainer}
            onSubmitCapture={handleSubmit(onSubmit)}
            autoComplete="off"
          >
            <InputCustom
              disabled={isLoading || disableInput}
              placeholder={t("placeholder:Username")}
              name={"username"}
              control={control}
              errors={errors.username?.message}
              margin_bottom={"8px"}
              validate_status={errors.username ? "error" : "success"}
              maxLength={16}
            />
            <InputPassword
              disabled={isLoading}
              placeholder={t("placeholder:Password")}
              name={"password"}
              control={control}
              margin_bottom="8px"
              error={errors.password?.message}
              validate_status={errors.password ? "error" : "success"}
              maxLength={15}
            />
            <InputPassword
              disabled={isLoading}
              placeholder={t("placeholder:ConfirmPassword")}
              name={"confirm_password"}
              control={control}
              margin_bottom="8px"
              autoComplete="new-password"
              error={errors.confirm_password?.message}
              validate_status={errors.confirm_password ? "error" : "success"}
              maxLength={15}
            />

            <InputPhoneNumber
              disabled={isLoading}
              name={"mobile"}
              control={control}
              error={errors.mobile?.message}
              margin_bottom="8px"
              validate_status={errors.mobile ? "error" : "success"}
            />

            <InputCustom
              disabled={isLoading}
              placeholder={t("placeholder:FullName")}
              name={"fullname"}
              margin_bottom="8px"
              control={control}
              errors={
                errors.fullname?.message
                  ? errors.fullname?.message
                  : isFocusFullname
                  ? t("validate:UsernameMatch")
                  : ""
              }
              validate_status={
                isFocusFullname || errors.fullname ? "error" : "success"
              }
              onClick={() => setIsFocusFullname(true)}
              onBlur={() => setIsFocusFullname(false)}
              onKeyDown={(e) => {
                const key = e.key;

                const regex = new RegExp("^[a-zA-Z\\s]+$");

                if (!regex.test(key) && key !== "Enter") {
                  e.preventDefault();
                }
              }}
            />
            <Popover
              placement="topRight"
              content={t("validate:Popover")}
              trigger="hover"
            >
              <InputCustom
                placeholder={t("placeholder:ReferralCode")}
                name={"referral"}
                margin_bottom="8px"
                control={control}
                disabled={true}
                errors={errors.referral?.message}
                validate_status={errors.referral ? "error" : "success"}
                className="referral-code"
              />
            </Popover>
            <button
              css={[
                SignupModalStyle.buttonSubmit,
                isValid && SignupModalStyle.buttonActive,
              ]}
              type="submit"
              disabled={isLoading || !isValid}
            >
              {isLoading ? <SyncOutlined spin /> : t("button:Register")}
            </button>
            <span css={SignupModalStyle.login}>
              {t("Already")}
              <span
                onClick={() => dispatch(setOpenModal("signin"))}
                style={{
                  marginLeft: "4px",
                }}
              >
                {t("LOGIN")}
              </span>
            </span>
          </Form>
          {contextHolder}
        </div>
      }
    />
  );
};
